/**
 * Google Sheets Snapshot Module
 *
 * Captures, stores, and restores Google Sheets spreadsheet state for versioning.
 * Snapshots are saved to IndexedDB with FIFO eviction (same as Excel).
 */

import { callAppsScript } from '../agents/sheets-tools'
import type { SheetSnapshot, Snapshot } from './snapshot'

// ============================================================================
// Capture Snapshot
// ============================================================================

/**
 * Capture the current state of the spreadsheet
 */
export async function captureSnapshot(): Promise<Snapshot> {
  const snapshotId = Date.now().toString()

  // Call Apps Script backend to capture all sheets
  const result = await callAppsScript<{
    success: boolean
    sheets: SheetSnapshot[]
  }>('captureAllSheets')

  if (!result.success) {
    throw new Error('Failed to capture snapshot from Google Sheets')
  }

  return {
    id: snapshotId,
    turnId: null,
    timestamp: Date.now(),
    sheets: result.sheets,
  }
}

// ============================================================================
// Restore Snapshot
// ============================================================================

/**
 * Restore the spreadsheet to a previous snapshot state
 */
export async function restoreWorkbookSnapshot(
  snapshot: Snapshot
): Promise<void> {
  console.log(`[RESTORE] Starting restore for snapshot ${snapshot.id}`)
  console.log(`[RESTORE] Snapshot has ${snapshot.sheets.length} sheets`)

  // Call Apps Script backend to restore sheets
  const result = await callAppsScript<{
    success: boolean
    sheetsRestored: number
  }>('restoreSheets', snapshot.sheets)

  if (!result.success) {
    throw new Error('Failed to restore snapshot in Google Sheets')
  }

  console.log(
    `[RESTORE] Successfully restored ${result.sheetsRestored} sheets to snapshot ${snapshot.id}`
  )
}
